import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDict } from '../i18n/dictionary';
import { shell } from '../i18n/shell';
import { dismissToast, useToastHost, useToasts } from '../lib/toast';

/**
 * The one place API results are shown. Mounted once in `main.tsx`, inside the
 * router so an action can be a `Link`, and outside the app layout so the login
 * page gets it too.
 *
 * A manual popover, so it lives in the top layer: a modal `Dialog` is there
 * too, and anything outside it sits under its backdrop whatever its z-index.
 * Re-showing on every change puts it back on top of a dialog opened since.
 * While a dialog is open it is portalled into it, since a modal leaves
 * everything outside itself inert, top layer or not.
 */
export function Toaster() {
  const toasts = useToasts();
  const t = useDict(shell);
  const host = useToastHost();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const toaster = ref.current;
    if (!toaster) return;
    if (toaster.matches(':popover-open')) toaster.hidePopover();
    if (toasts.length > 0) toaster.showPopover();
  }, [toasts, host]);

  const list = (
    <div ref={ref} popover="manual" className="toaster">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          className={toast.kind === 'error' ? 'toast-error' : 'toast-success'}
        >
          <span className="toast-message">
            {toast.message}{' '}
            {toast.action && (
              <Link
                to={toast.action.to}
                className="toast-link"
                onClick={() => dismissToast(toast.id)}
              >
                {toast.action.label}
              </Link>
            )}
          </span>
          <button
            type="button"
            className="toast-close"
            aria-label={t.dismiss}
            onClick={() => dismissToast(toast.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
  return host ? createPortal(list, host) : list;
}
